export const PLAN_PREVIEW_KEY = 'ology-plan-preview';

export type PlanId = 'practice' | 'clinic' | 'system';

/** Plans a customer can buy in the app. Health System stays sales-led. */
export const SELF_SERVE_PLAN_IDS: PlanId[] = ['practice', 'clinic'];

export type FeatureId =
  | 'messaging'
  | 'campaigns'
  | 'genericPro'
  | 'validatedInstruments'
  | 'followUp'
  | 'proReports'
  | 'integrations'
  | 'multiSite'
  | 'ai';

export interface PaywallPlan {
  id: PlanId;
  name: string;
  /** null = custom pricing; show "Contact us" in UI */
  monthlyPrice: number | null;
  audience: string;
  /** Included practitioner logins; null = unlimited (Health System) */
  includedPractitioners: number | null;
  /** Included non-practitioner staff logins; null = unlimited */
  includedStaff: number | null;
  seats: string;
  summary: string;
  highlighted?: boolean;
}

export interface PaywallFeature {
  id: FeatureId;
  name: string;
  detail: string;
  minimumPlan: PlanId;
}

export const PAYWALL_PLANS: PaywallPlan[] = [
  {
    id: 'practice',
    name: 'Practice',
    monthlyPrice: 99,
    audience: 'A single practice starting outcomes collection',
    includedPractitioners: 1,
    includedStaff: 1,
    seats: '1 practitioner & 1 staff member included; +$49/mo each additional',
    summary: 'Every feature—messaging, campaigns, PRO tools, reports, integrations, and AI.',
  },
  {
    id: 'clinic',
    name: 'Clinic',
    monthlyPrice: 249,
    audience: 'A clinic running scored follow-up after procedures',
    includedPractitioners: 5,
    includedStaff: 5,
    seats: '5 practitioners & 5 staff included; +$49/mo each additional',
    summary: 'Every feature—messaging, campaigns, PRO tools, reports, integrations, and AI.',
    highlighted: true,
  },
  {
    id: 'system',
    name: 'Health System',
    monthlyPrice: null,
    audience: 'Several locations that need one view of outcomes',
    includedPractitioners: null,
    includedStaff: null,
    seats: 'Unlimited practitioners & staff',
    summary: 'Every feature—messaging, campaigns, PRO tools, reports, integrations, and AI.',
  },
];

export const PAYWALL_FEATURES: PaywallFeature[] = [
  {
    id: 'messaging',
    name: 'Patient messaging',
    detail: 'Two-way texts with contacts',
    minimumPlan: 'practice',
  },
  {
    id: 'campaigns',
    name: 'Campaigns',
    detail: 'Education and reminder sequences',
    minimumPlan: 'practice',
  },
  {
    id: 'genericPro',
    name: 'Generic outcome survey',
    detail: 'One custom patient-reported survey',
    minimumPlan: 'practice',
  },
  {
    id: 'validatedInstruments',
    name: 'Validated instruments',
    detail: 'ASES, Oxford Knee Score, and Oxford Hip Score',
    minimumPlan: 'practice',
  },
  {
    id: 'followUp',
    name: 'Automated follow-up',
    detail: 'Send scores on a schedule after a procedure',
    minimumPlan: 'practice',
  },
  {
    id: 'proReports',
    name: 'PRO score reports',
    detail: 'Organization view of scores over time',
    minimumPlan: 'practice',
  },
  {
    id: 'integrations',
    name: 'EHR connection',
    detail: 'ModMed and scheduling integrations',
    minimumPlan: 'practice',
  },
  {
    id: 'multiSite',
    name: 'Multi-site reporting',
    detail: 'Compare locations on one subscription',
    minimumPlan: 'practice',
  },
  {
    id: 'ai',
    name: 'AI assist',
    detail: 'Draft replies and summarize message threads',
    minimumPlan: 'practice',
  },
];

export function planById(id: PlanId): PaywallPlan {
  return PAYWALL_PLANS.find((plan) => plan.id === id) ?? PAYWALL_PLANS[0];
}

/** All subscription tiers include the full feature set; plans differ by price and team size. */
export function planIncludes(_plan: PlanId, feature: FeatureId): boolean {
  return PAYWALL_FEATURES.some((item) => item.id === feature);
}

export function formatPlanPrice(monthlyPrice: number | null): string {
  if (monthlyPrice == null) {
    return 'Contact us';
  }
  return `$${monthlyPrice}`;
}

export function planShowsMonthlyPrice(plan: PaywallPlan): boolean {
  return plan.monthlyPrice != null;
}

export function readPreviewPlan(): PlanId {
  try {
    const stored = localStorage.getItem(PLAN_PREVIEW_KEY);
    if (stored === 'practice' || stored === 'clinic' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage is unavailable during some renders; fall through to the default.
  }
  return 'practice';
}

export function writePreviewPlan(id: PlanId): void {
  localStorage.setItem(PLAN_PREVIEW_KEY, id);
}
